import Dexie from 'dexie';
import type { Table } from 'dexie';
import { config } from '../config';
import { StorageError } from '../app/errors';
import type { HypothesisRow, LogEntryRow, ObservationRow } from '../types';
import { applyMigrations } from './migrations';

/**
 * Design §1.2 — `data/`: **the only place `dexie` is imported.**
 *
 * Build unit U-0 defines the harness database, not the product database. See the note on
 * `HARNESS_DB_NAME` in `config/constants.ts` for why they are separate. U-3 owns the product
 * schema and starts its own numbering at `v001` on its own database name.
 *
 * What matters for the measurement: the two stores this database declares carry **the exact
 * indexes design §3.4 specifies for them**. Index maintenance is a real part of an IndexedDB
 * commit's cost, and measuring against index-free stores would produce an optimistic number and
 * leave A-1 unanswered — which is the one outcome this unit cannot afford.
 */

/**
 * Bulk storage filler. Lives here rather than in `types/` because it carries a `Blob`, and
 * `types/` is compiled without the DOM library so that the pure layers stay pure.
 */
export interface BulkBlobRow {
  id: string;
  bytes: number;
  blob: Blob;
}

/** Persisted harness runs. Stored as a JSON-ish row; Dexie structured-clones it. */
export interface StoredRun {
  id: string;
  startedAt: number;
  condition: string;
  /** The full `RunRecord`. Kept opaque here so `data/` does not depend on harness types. */
  payload: unknown;
}

/**
 * One `MediaRecorder` `dataavailable` payload, persisted as it arrives (design §8.2 step 2).
 *
 * Blob-typed, so it lives here rather than in `types/` — the same rule as `BulkBlobRow`. `index` is
 * the **arrival** index and is half the compound primary key: assembly reads by index, never by
 * insertion order, because chunk writes are async and can complete out of order.
 */
export interface AudioChunkRow {
  segmentId: string;
  index: number;
  blob: Blob;
  sizeBytes: number;
  /** `Blob.type` as the recorder emitted it. AUD-1 stores what was emitted, never a guess. */
  type: string;
  receivedAt: number;
}

/**
 * One recording, with its verbatim assembly.
 *
 * `blob` is the chunk sequence concatenated in arrival order with **no transcoding** (AUD-1) and
 * `mime` is `recorder.mimeType`. `complete=false` means finalize never ran — the design's §8.5
 * recovery pass assembles and probes these on the next launch.
 */
export interface AudioSegmentRow {
  id: string;
  startedAt: number;
  complete: boolean;
  mime: string;
  chunkCount: number;
  sizeBytes: number;
  /** Null until finalize (or the recovery pass) assembles it. */
  blob: Blob | null;
  /** The full `SegmentRecord`. Opaque here so `data/` does not depend on harness types. */
  payload: unknown;
}

/** A persisted U-1 spike run. Carries stats only; the audio lives in `audioSegments`. */
export interface AudioRunRow {
  id: string;
  startedAt: number;
  /** The full `AudioRunRecord`. */
  payload: unknown;
}

export class HarnessDb extends Dexie {
  declare observations: Table<ObservationRow, string>;
  declare hypotheses: Table<HypothesisRow, string>;
  declare logEntries: Table<LogEntryRow, number>;
  declare bulkBlobs: Table<BulkBlobRow, string>;
  declare runs: Table<StoredRun, string>;
  // U-1 (v002)
  declare audioChunks: Table<AudioChunkRow, [string, number]>;
  declare audioSegments: Table<AudioSegmentRow, string>;
  declare audioRuns: Table<AudioRunRow, string>;

  constructor(name: string = config.HARNESS_DB_NAME) {
    super(name);
    applyMigrations(this);
  }
}

let instance: HarnessDb | null = null;

/** The process-wide connection. `data/` owns its lifecycle; nothing else constructs a `Dexie`. */
export function db(): HarnessDb {
  instance ??= new HarnessDb();
  return instance;
}

/**
 * Opens the database and reports how long the open took.
 *
 * The open cost is reported separately from any trial because it is not part of a tap: the product
 * warms the connection on entering an in-match state and on `visibilitychange → visible`
 * (design §5.1, "Protections"). Folding it into a commit figure would make the cold condition
 * look worse than the product will actually behave.
 *
 * @throws {StorageError} if the database cannot be opened. Surfaced, never swallowed (NFR-8) —
 *   a harness that silently ran against a closed database would report zeros as success.
 */
export async function openDb(now: () => number): Promise<{ db: HarnessDb; openMs: number }> {
  const handle = db();
  const t0 = now();
  try {
    await handle.open();
  } catch (cause) {
    throw new StorageError('Could not open the harness IndexedDB database.', {
      cause,
      context: { name: handle.name, version: config.HARNESS_DB_VERSION },
    });
  }
  return { db: handle, openMs: now() - t0 };
}

/**
 * Closes and reopens the connection — the "cold" condition's mechanism.
 *
 * This is a *connection*-cold, not a *process*-cold. A genuinely cold launch (app killed, PWA
 * relaunched from the home screen) is stronger and cannot be produced from inside the page; the
 * on-device procedure in the README covers it, and `TrialSample.sinceLoadMs` is what lets a
 * genuinely-cold first trial be identified in the exported data.
 */
export async function reopenCold(now: () => number): Promise<{ db: HarnessDb; openMs: number }> {
  const handle = db();
  handle.close();
  const t0 = now();
  try {
    await handle.open();
  } catch (cause) {
    throw new StorageError('Could not reopen the harness database for the cold condition.', {
      cause,
      context: { name: handle.name },
    });
  }
  return { db: handle, openMs: now() - t0 };
}

/**
 * Design §5.1 "Protections" — the product warms the connection with a no-op `readwrite` on
 * entering an in-match state. The warm condition reproduces that; the cold condition does not.
 */
export async function warmConnection(): Promise<void> {
  const handle = db();
  try {
    await handle.transaction('rw', handle.observations, handle.hypotheses, async () => {
      await handle.hypotheses.limit(1).toArray();
    });
  } catch (cause) {
    throw new StorageError('Connection warm-up transaction failed.', { cause });
  }
}

/** Wipes every harness store. Used before re-seeding and by the "Reset" control. */
export async function resetAll(): Promise<void> {
  const handle = db();
  try {
    await handle.transaction(
      'rw',
      handle.observations,
      handle.hypotheses,
      handle.bulkBlobs,
      async () => {
        await handle.observations.clear();
        await handle.hypotheses.clear();
        await handle.bulkBlobs.clear();
      },
    );
  } catch (cause) {
    throw new StorageError('Could not clear the harness database.', { cause });
  }
}

/** Deletes the entire database, so a venue run leaves nothing behind on the phone. */
export async function destroyDb(): Promise<void> {
  const handle = db();
  try {
    handle.close();
    await handle.delete();
  } catch (cause) {
    throw new StorageError('Could not delete the harness database.', { cause });
  } finally {
    instance = null;
  }
}

/** Test seam only — lets a test point the schema at an isolated database name. */
export function __setDbForTests(next: HarnessDb | null): void {
  instance = next;
}
